import {
  Box,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'src/redux-store/appStore';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { FormikUploadFile } from 'src/ui-component/FormikFormLibrary/FormikUploadFile';
import '../../../../../App.css';
import InfoIcon from '@mui/icons-material/Info';
import CustomButtonOutlined from 'src/components/CustomButton/CustomButtonOutlined';
import { useState } from 'react';
import FormikTextField from 'src/ui-component/FormikFormLibrary/FormikTextField';
import CustomButtonContained from 'src/components/CustomButton/CustomButtonContained';
import { FormikDatePicker } from 'src/ui-component/FormikFormLibrary/FormikDatePicker';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import {
  PaymentApplicationDetails,
  PaymentSummary
} from 'src/types/RoadPermit/roadPermit.type';
import { getFeesForCurrentPermit } from 'src/pages/Permits/common/getFeesForCurrentPermit';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { handleError } from 'src/redux-store/slices/errorSlice';
import DownloadPopUp from 'src/ui-component/DownloadApplicationForm/DownloadPopUp';

interface FileDataType {
  downloadURL: string;
  fileName: string;
}

interface EditApplicationForUserProps {
  handleBlockLotsList: (val) => void;
  setClickedOn: any;
  handleSubmitOnUpdateAndPayment: (val) => void;
  blockLotsOptions: [];
  setSwitchTo: any;
  treePermitFormData;
  setTreePermitFormData;
  applicationData;
  handleUpdate;
}

function EditApplicationForUser({
  handleBlockLotsList,
  blockLotsOptions,
  setSwitchTo,
  treePermitFormData,
  setTreePermitFormData,
  applicationData,
  handleSubmitOnUpdateAndPayment,
  setClickedOn
}: EditApplicationForUserProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { townshipId, permit } = useParams();
  const currentPermitDetails = useSelector(
    (state: RootState) => state?.currentPermit?.currentPermitDetails
  );
  const currentTownshipDetails = useSelector(
    (state: RootState) => state?.township?.townshipDetails
  );
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const basePath = `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}`;
  const isManager = currentPermitDetails.currentPermitUserRole === 'Manager';
  const [conditionChecks, setConditionChecks] = useState(
    {} as { ToggleApplicationDetails: boolean; promiseCheck: boolean }
  );
  const [preferenceDescription, setPreferenceDescription] =
    useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [dataOnFileChange, setDataOnFileChange] = useState<FileDataType>();
  const [replacementMethodData, setReplacementMethodData] = useState(
    {} as { treeReplacementMethod: string }
  );

  //method to handle change in form fields
  const handleChange = (val, e) => {
    const { name } = e;
    if (name === 'blockLot') handleBlockLotsList(val);
    else
      setTreePermitFormData({
        ...treePermitFormData,
        [name]: val
      });
  };

  //form fields using formik
  const formFields: Fields[] = [
    {
      fieldName: 'blockLot',
      fieldLabel: 'Block Lot OR Address where tree(s) are located',
      fieldType: 'autocomplete',
      isErrorField: false,
      placeHolder: 'Enter minimum 3 digits',
      required: true,
      initialValue: treePermitFormData?.blockLot,
      disabled: !isManager,
      options: blockLotsOptions || [
        {
          label: treePermitFormData?.blockLot,
          BlockLot: treePermitFormData?.blockLot
        }
      ],
      dependency: handleChange
    },
    {
      fieldName: 'owner',
      fieldLabel: 'Owner',
      fieldType: 'text',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.owner?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'treeAddress',
      fieldLabel: 'Address where tree(s) are located',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.treeAddress?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'nearBy',
      fieldLabel: 'Nearest Cross Street',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.nearBy?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'phone',
      fieldLabel: 'Phone Number',
      fieldType: 'mobile',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.phone,
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'numberOfTrees',
      fieldLabel: 'Number of Trees to be Removed',
      fieldType: 'number',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.numberOfTrees,
      lengthLimit: 3,
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'purpose',
      fieldLabel: 'Purpose of Removal',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.purpose?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'lotSize',
      fieldLabel: 'Lot Size (S.F./ Acreage)',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.lotSize?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'areaConfirmationCheck',
      fieldLabel:
        'Are trees being removed from a restricted area? (i.e. Wetlands, Easements, Buffers, etc.)',
      fieldType: 'radio',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.areaConfirmationCheck,
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'Unsure', label: 'Unsure' }
      ],
      disabled: false,
      dependency: handleChange
    }
  ];

  //radio button options for tree replacement method
  const radioBtns = [
    {
      info: `If you choose to replace the tree(s) you are removing, you will have 1 calendar year from submitting this permit request to do so. 
      The replacement trees can be planted anywhere on your property.
      A map or drawing of the anticipated location of the trees to be replanted must be uploaded, and reviewed, prior to permit being issued.`,
      value: 'Replant',
      label: 'Applicant to Replant Replacement Tree(s)',
      isDisabled:
        applicationData?.MethodOfTreeReplacement === 'TownshipTreeReplacement'
    },
    {
      info: `If space is limited or you do not wish to replant, you may choose to pay the replacement 
      dollar amount which is equal to the value of the replacement trees. Click on Tree Replacement Fee option to see the amount.`,
      value: 'Fee',
      label: 'Applicant to pay Tree Replacement Fee',
      isDisabled:
        applicationData?.MethodOfTreeReplacement === 'TownshipTreeReplacement'
    },
    {
      value: 'Replace',
      label: 'Township to Replace Tree(s)',
      isDisabled:
        applicationData?.MethodOfTreeReplacement === 'VendorTreeReplacement'
    }
  ];

  //method to handle upload document
  const handleUploadDocument = async () => {
    if (applicationData?.ApplicationID) {
      setLoading(true);
      const reqObj = {
        ApplicationID: applicationData?.ApplicationID,
        PermitID: permit,
        DocumentFileUpload: dataOnFileChange?.fileName
      };
      const url = `/uploadPDF/insert_PDF`;
      const response = await httpRequest('POST', url, reqObj);
      if (response?.status === 'success') {
        toast.success('Document uploaded successfully.');
        setFile(null);
      } else if (response?.type !== 'NETWORK_ERROR') {
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
      setLoading(false);
      return response?.status === 'success';
    }
  };

  //method to handle tree preference
  const handleTreePreference = async (preference) => {
    setLoading(true);
    const DocUploaded = !preference && handleUploadDocument();
    if (preference || DocUploaded) {
      const data1 = {
        VendorProcess: applicationData?.MethodOfTreeReplacement,
        VendorPreference: preferenceDescription
      };
      const data2 = {
        VendorProcess: applicationData?.MethodOfTreeReplacement
      };
      const response = await httpRequest(
        'POST',
        `/tree_permit_application/${applicationData?.ApplicationID}/process-notify`,
        preference ? data1 : data2
      );
      if (response?.status === 'success') {
        setLoading(false);
        setPreferenceDescription('');
        toast.success(
          preference
            ? 'Preference added successfully.'
            : 'Details added successfully.'
        );
        navigate(`${basePath}/application-list`);
      } else if (response?.type !== 'NETWORK_ERROR') {
        setLoading(false);
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    } else {
      setLoading(false);
    }
  };

  //method to get total number of trees to replant
  const getTotalNoOfTreesToReplant = () => {
    let total;
    if (applicationData && applicationData?.ReplacementTreeCaliper) {
      total = JSON.parse(applicationData?.ReplacementTreeCaliper)?.reduce(
        (accumulator, currentValue) => {
          return accumulator + parseInt(currentValue.Trees, 10);
        },
        0
      );
    }
    return total;
  };

  //method to get form buttons
  const getFormButtons = () => {
    let buttonsCollection = ['Cancel'];
    if (applicationData?.ApplicationStatus) {
      if (applicationData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Update'];
      }
      if (applicationData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Proceed to Payment'];
      }
    }
    return buttonsCollection;
  };

  //method to handle file change
  const handleFileChange = async (event) => {
    const selectedFile = event?.[0] || null;
    const formData = new FormData();
    formData.append('uploadPDF', selectedFile);

    const url = `/uploadPDF/?permitName=Tree`;
    const response = await httpRequest('POST', url, formData, {
      'Content-Type': 'multipart/form-data'
    });

    if (response?.status === 'success') {
      setDataOnFileChange(response?.data);
    } else if (response?.type !== 'NETWORK_ERROR') {
      toast.error(response?.message);
      dispatch(handleError(response?.message));
    }
    setFile(event?.target?.files?.[0] || event?.[0]);
  };

  //method to get application fee
  const handleGetApplicationFee = async () => {
    setLoading(true);
    let charges;
    const response = await httpRequest(
      'GET',
      `/configuration/fee_details?PermitID=${permit}`
    );
    if (response?.status === 'success') {
      const feeObject = getFeesForCurrentPermit(response.data);
      charges = feeObject?.CreditCardFee || 0;
      setLoading(false);
    } else if (response?.type !== 'NETWORK_ERROR') {
      setLoading(false);
      toast.error(response?.message);
    }
    return response?.status === 'success' ? charges : 0;
  };

  //method to handle pay replacement fee
  const handlePayreplacementFee = async () => {
    const charges = await handleGetApplicationFee();
    if (charges) {
      const isTreeReplacementPayment = true;
      const paymentSummary: PaymentSummary = {
        fees: {
          treeReplacementFee: Number(applicationData?.AdditionalEscrowFee)
        },
        taxRate: Number(charges)
      };
      const paymentApplicationDetails: PaymentApplicationDetails = {
        applicationDetails: {
          userName: `${userData?.FirstName} ${userData?.LastName}`,
          applicationNumber: null,
          applicationId: applicationData?.ApplicationID,
          applicationStatus: applicationData?.ApplicationStatus,
          MethodOfTreeReplacement: applicationData?.MethodOfTreeReplacement
        }
      };
      navigate(`/payment-methods`, {
        state: {
          paymentSummary,
          paymentApplicationDetails,
          isTreeReplacementPayment
        }
      });
    }
  };

  const ReplacementMethodsContent = () => {
    return (
      <Grid container gap={2} mt={2}>
        <Grid item xs={12} sm={12}>
          <Typography variant="h6">
            Please select one of the following options indicating your desired
            method of tree replacement:
          </Typography>
          <RadioGroup
            name="treeReplacementMethod"
            onChange={(e) =>
              setReplacementMethodData({
                ...replacementMethodData,
                treeReplacementMethod: e.target.value
              })
            }
            row
            sx={{ display: 'flex' }}
          >
            {radioBtns.map((option, i) => (
              <>
                <FormControlLabel
                  key={option.value}
                  sx={{
                    ml: i === 0 ? '' : { xs: 0, sm: 8 },
                    mr: { xs: 0, sm: 'auto' }
                  }}
                  value={option.value}
                  control={
                    <Radio
                      disabled={option?.isDisabled}                     
                    />
                  }
                  label={option.label}
                />
                {option?.info ? (
                  <Tooltip title={option?.info} arrow>
                    <IconButton sx={{ px: 0 }}>
                      <InfoIcon color="primary" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </>
            ))}
          </RadioGroup>
        </Grid>
        {replacementMethodData?.treeReplacementMethod === 'Replant' ? (
          <>
            <Grid item xs={12} sm={12}>
              <Typography variant="h6" my={1}>
                Replanting of trees as per permit assessment.
              </Typography>
              <Typography color={'#858796'}>
                You are required to replant given below -:
              </Typography>
              {applicationData &&
                JSON.parse(applicationData?.ReplacementTreeCaliper)?.map(
                  (detail, index) => {
                    return (
                      <Typography color={'#858796'} my={1} key={index}>
                        <b className="theme_text_color">{`(${detail?.Trees})`}</b>{' '}
                        tree(s) at{' '}
                        <b className="theme_text_color">{`(${detail?.Caliper?.replace(
                          /\\"/g,
                          '"'
                        )})`}</b>{' '}
                        caliper.
                      </Typography>
                    );
                  }
                )}             
              <Box pt={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name={'ToggleApplicationDetails'}
                      color="primary"
                      onChange={(e) =>
                        setConditionChecks({
                          ...conditionChecks,
                          promiseCheck: e.target.checked
                        })
                      }
                    />
                  }
                  label={`I promise to replant ${getTotalNoOfTreesToReplant()} tree(s) on 
                  my property within one year from the date my permit is approved.`}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} mb={{ xs: 2, sm: 0 }}>
              <Typography variant="h6" mb={2}>
                Click on Upload Documents button and upload a drawing indicating
                the location(s) of the replacement tree(s).
              </Typography>
              <FormikUploadFile
                disabled={false}
                multiSelect
                listUI
                label={'Upload Documents'}
                name="uploadTreeReplacementDocs"
                placement="start"
                handleChange={(e) => handleFileChange(e)}
              />
            </Grid>
          </>
        ) : replacementMethodData?.treeReplacementMethod === 'Fee' ? (
          <Typography
            fontWeight={'bold'}
            color={'#6e759f'}
            fontSize={15}
            mb={{ xs: 2, sm: 0 }}
          >
            Tree Replacement Fee ${applicationData?.AdditionalEscrowFee || 0.0}
          </Typography>
        ) : replacementMethodData?.treeReplacementMethod === 'Replace' ? (
          <Box>
            <Typography fontWeight={'500'} fontSize={14}>
              Replanting of trees as per permit assessment.
            </Typography>           
            <Typography fontWeight={'500'} fontSize={14}>
              Township are required to replant{' '}
              <b>({applicationData?.NoOfTrees})</b> tree(s).
            </Typography>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    name={'replantAcknoCheck'}
                    color="primary"
                  />
                }
                label={
                  'I acknowledge that all requested trees will be planted within the Township easement and not on private property.'
                }
              />
            </Box>

            <Typography fontWeight={'500'} fontSize={14}>
              (Please note that all tree requests will be honored in the order
              they are recieved, however this does not guarantee a tree will be
              planted as the program is solely based on available funding.)
            </Typography>
            <Typography fontWeight={'600'} fontSize={13} my={1}>
              Please describe, if any, your preference on the type of tree
            </Typography>
            <Grid md={6}>
              <FormikTextField
                name={'treePreference '}
                label={'Description'}
                type={'note'}
                handleChange={(e) => {
                  setPreferenceDescription(e.target.value);
                }}
              />
            </Grid>
          </Box>
        ) : null}
      </Grid>
    );
  };

  const handleContent = () => {
    let content;
    if (
      !isManager &&
      applicationData?.MethodOfTreeReplacement &&
      applicationData?.ApplicationStatus === 'Await Vendor'
    ) {
      content = ReplacementMethodsContent();
    }
    return content;
  };

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Box mb={{ xs: 8 }}>
      <Card sx={{ p: { xs: 1.5, sm: 3 }, pt: 0 }}>
        <Box sx={{ mt: 1 }} className="newApp">
          <Box
            pb={2}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignContent: 'center'
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  name={'ToggleApplicationDetails'}
                  color="primary"
                  onChange={(e) =>
                    setConditionChecks({
                      ...conditionChecks,
                      ToggleApplicationDetails: e.target.checked
                    })
                  }
                />
              }
              label={'Hide/Show Application Details'}
            />
            <Box>
              <DownloadPopUp
                title={'Tree Removal Application'}
                ApplicationDetails={{
                  BlockLot: treePermitFormData?.blockLot,
                  FullName: treePermitFormData?.owner?.trim(),
                  AddressWhereTreeAreLocated:
                    treePermitFormData?.treeAddress?.trim(),
                  NearestCrossStreet: treePermitFormData?.nearBy?.trim(),
                  phoneNumber: treePermitFormData?.phone,
                  NumberOfTreesToBeRemoved: treePermitFormData?.numberOfTrees,
                  'Lot Size (S.F./ Acreage)':
                    treePermitFormData?.lotSize?.trim(),
                  PurposeOfRemoval: treePermitFormData?.purpose?.trim(),
                  'Are trees being removed from a restricted area? (i.e. Wetlands, Easements, Buffers, etc.)':
                    treePermitFormData?.areaConfirmationCheck,
                  'I have read and understand Township Code Chapter 457 Trees and Invasive Species.':
                    <Checkbox />,
                  'I understand that the Tree Removal Contractor I will retain is registered.':
                    <Checkbox />,
                  'All trees to be removed must be field marked prior to submitting your application.':
                    <Checkbox />
                }}
              />
            </Box>
          </Box>

          {conditionChecks?.ToggleApplicationDetails ? (
            <FormikForm
              allFields={[...formFields]}
              buttonTitle={getFormButtons()}
              fullScreen={false}
              isEditModel={false}
              heading={''}
              setClickedOn={setClickedOn}
              clickProp
              defaultEnabledButton={['Proceed to Payment', 'Approve']}
              isButtonHide={applicationData?.ApplicationStatus === 'Approved'}
              formMethod={handleSubmitOnUpdateAndPayment}
              buttonPlacement={'right'}
              handleOtherButton={() => {
                if (applicationData?.ApplicationStatus === 'Draft')
                  navigate(`${basePath}/draft-application`);
                else navigate(`${basePath}/application-list`);
              }}
            />
          ) : null}
        </Box>
        <Grid item xs={12} container alignItems="center" spacing={2}>
          <Grid xs={12} item p={{ xs: 0, sm: 'auto' }}>
            <CustomButtonOutlined
              sx={{
                mt: { xs: 2, sm: 'auto' },
                width: { xs: '100%', sm: 'auto' }
              }}
              title={'Upload/View Document'}
              startIcon={<CloudUploadIcon />}
              onClick={() => {
                setSwitchTo('Upload/View Document');
              }}
            />
            {applicationData?.ApplicationStatus !== 'Draft' && (
              <>
                <CustomButtonOutlined
                  sx={{
                    ml: { xs: 0, sm: 2 },
                    mt: { xs: 1.5, sm: 0 },
                    width: { xs: '100%', sm: 'auto' }
                  }}
                  title={'View Message History'}
                  onClick={() => {
                    setSwitchTo('View Message History');
                  }}
                />
                <CustomButtonOutlined
                  sx={{
                    ml: { xs: 0, sm: 2 },
                    mt: { xs: 1.5, sm: 0 },
                    width: { xs: '100%', sm: 'auto' }
                  }}
                  title={'View Payment History'}
                  onClick={() => {
                    setSwitchTo('View Payment History');
                  }}
                />
              </>
            )}
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Typography variant="h4" color={'#6e759f'}>
          {`${currentTownshipDetails?.name?.toUpperCase()} TREE REPLACEMENT SELECTION FORM`}
        </Typography>
        <Typography
          color="gray"
          mb={3}
        >{`(Only for Admin/Manager)`}</Typography>
        <Grid xs={12} container spacing={2} className="00000000000">
          <Grid item xs={12} md={6}>
            <Box mb={2}>
              <FormikTextField
                name={'conditions'}
                label={treePermitFormData?.conditions ? '' : 'Conditions'}
                type={'note'}
                fieldValue={{ value: treePermitFormData?.conditions }}
                handleChange={handleChange}
                disabled={!isManager}
              />
            </Box>

            <FormikDatePicker
              label={'Inspection Date'}
              onChange={(e) => {
                setTreePermitFormData({
                  ...treePermitFormData,
                  inspectionDate: e
                });
              }}
              disabled={!isManager}
              initialValue={treePermitFormData?.inspectionDate || 'mm/dd/yyyy'}
              fieldValue={{
                value: treePermitFormData?.inspectionDate || 'mm/dd/yyyy'
              }}
              disablePast={true}
            />
          </Grid>
        </Grid>
        <Typography
          mt={2}
          color={'green'}
          mb={{ xs: 2, sm: 0 }}
          fontSize={15}
          fontWeight={500}
        >
          {applicationData?.SelectionOption &&
          applicationData?.ApplicationStatus === 'Await CO'
            ? 'Conservation officer is reviewing your application.'
            : applicationData?.ApplicationStatus === 'Approved'
            ? 'Application has been approved.'
            : null}
        </Typography>
        {handleContent()}
        <Box display={'flex'} justifyContent={'end'}>
          <CustomButtonOutlined
            title={'Return To Home'}
            onClick={() => {
              navigate(`/dashboard/${townshipId}`);
            }}
          />
          <CustomButtonOutlined
            sx={{ ml: 2 }}
            title={'Cancel'}
            onClick={() => {
              if (applicationData?.ApplicationStatus === 'Draft')
                navigate(`${basePath}/draft-application`);
              else navigate(`${basePath}/application-list`);
            }}
          />
          {!isManager &&
          applicationData?.MethodOfTreeReplacement &&
          applicationData?.ApplicationStatus === 'Await Vendor' ? (
            <CustomButtonContained
              sx={{ ml: 2 }}
              title={
                replacementMethodData?.treeReplacementMethod !== 'Fee'
                  ? 'Submit'
                  : 'Proceed To Pay'
              }
              onClick={() =>
                replacementMethodData?.treeReplacementMethod !== 'Fee'
                  ? handleTreePreference(
                      preferenceDescription === '' ? false : true
                    )
                  : handlePayreplacementFee()
              }
              disabled={
                replacementMethodData?.treeReplacementMethod !== 'Fee'
                  ? preferenceDescription === '' &&
                    !file &&
                    !conditionChecks?.promiseCheck
                  : !applicationData?.AdditionalEscrowFee
              }
            />
          ) : null}
        </Box>
      </Card>
    </Box>
  );
}

export default EditApplicationForUser;
